// ============================================
// FILTRORUBRO.JS — el filtro por rubro (familia) para cualquier lista de productos
// ============================================
// RUBROS Commit 2 (2026-10-16, decision de Ricardo). El MISMO patron en las cuatro pantallas:
// Listas pactadas, Colecciones, Mis Productos y el panel de cotizar dentro del expediente.
//
//   - Convive con el buscador inteligente: recibe los items que el buscador ya dejo visibles y
//     filtra ENCIMA. Se puede buscar "tplink" Y filtrar "Redes" a la vez.
//   - Enseña el conteo por rubro, como los filtros de CCTV de La Tienda, contado sobre lo que el
//     buscador dejo (si buscas, los numeros cambian contigo).
//   - Los productos sin familia NO se esconden: los de Mis Productos salen como "Material general"
//     y los de Syscom como "Otros". Nunca un producto desaparece por no tener rubro.
//   - Si el rubro elegido se queda en cero, se dice con texto (`textoVacio`), nunca pantalla en
//     blanco. Si el rubro elegido deja de existir en la lista (p. ej. tras buscar), el chip sigue
//     visible con (0) para que se pueda quitar.
//   - Celular primero: una fila de chips con scroll horizontal, 40 px de alto, sin `vh`.
//
// Uso:
//   const familias = useFamilias();
//   const rubro = useFiltroRubro(busq.visibles, rubroDeItem, familias);   // rubroDeItem opcional
//   <FiltroRubro filtro={rubro} />
//   {rubro.visibles.map(...)}
//   {rubro.visibles.length === 0 && <AvisoVacioRubro filtro={rubro} busqueda={busq.q} />}
import React, { useMemo, useState } from 'react';
import useFamilias, { etiquetaFamilia } from '../hooks/useFamilias';
import { tokens } from '../styles/tokens';

export const RUBRO_GENERAL = '__general';   // producto de Beep sin familia: material general
export const RUBRO_OTROS   = '__otros';     // producto de Syscom sin familia Beep: otros
const TODOS = '__todos';

// De que rubro es un item, por defecto: su `familia`; sin ella, segun de donde viene.
export function rubroDeItem(it) {
  if (!it) return RUBRO_GENERAL;
  if (it.familia) return it.familia;
  const origen = it.tipo || it.fuente || '';
  return origen === 'syscom_producto' ? RUBRO_OTROS : RUBRO_GENERAL;
}

export function etiquetaRubro(id, familias) {
  if (id === RUBRO_GENERAL) return 'Material general';
  if (id === RUBRO_OTROS) return 'Otros';
  if (!id) return 'Sin rubro';
  return etiquetaFamilia(id, familias);
}

// Orden canonico: el de las 13 familias, y los dos cajones sin familia al final.
function ordenDe(id, familias) {
  if (id === RUBRO_GENERAL) return 1000;
  if (id === RUBRO_OTROS) return 1001;
  const i = (familias || []).findIndex(f => f.id === id);
  return i < 0 ? 900 : i;
}

export function useFiltroRubro(items, rubroDe = rubroDeItem, familiasExternas = null) {
  const familiasHook = useFamilias();
  const familias = familiasExternas || familiasHook;
  const [rubro, setRubro] = useState(TODOS);
  const lista = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  const conteos = useMemo(() => {
    const m = new Map();
    for (const it of lista) { const r = rubroDe(it); m.set(r, (m.get(r) || 0) + 1); }
    const opciones = [...m.entries()]
      .map(([id, n]) => ({ id, n, etiqueta: etiquetaRubro(id, familias) }))
      .sort((a, b) => ordenDe(a.id, familias) - ordenDe(b.id, familias));
    // El rubro elegido sigue en la fila aunque se haya quedado en cero (para poder quitarlo).
    if (rubro !== TODOS && !m.has(rubro)) opciones.push({ id: rubro, n: 0, etiqueta: etiquetaRubro(rubro, familias) });
    return opciones;
  }, [lista, rubroDe, familias, rubro]);

  const visibles = useMemo(
    () => (rubro === TODOS ? lista : lista.filter(it => rubroDe(it) === rubro)),
    [lista, rubro, rubroDe]);

  const activo = rubro !== TODOS;
  return {
    rubro, setRubro, activo, limpiar: () => setRubro(TODOS),
    visibles, conteos, total: lista.length,
    etiqueta: activo ? etiquetaRubro(rubro, familias) : null,
    TODOS,
  };
}

export default function FiltroRubro({ filtro, style }) {
  const { rubro, setRubro, conteos, total, TODOS: T } = filtro;
  // Con un solo rubro en la lista no hay nada que filtrar: la fila sobra.
  if (!conteos.length || (conteos.length === 1 && conteos[0].n === total && rubro === T)) return null;
  const chip = (activo) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
    minHeight: 40, padding: '0 12px', borderRadius: 999, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 13, fontWeight: activo ? 700 : 500, whiteSpace: 'nowrap',
    border: `1px solid ${activo ? tokens.primary : tokens.border}`,
    background: activo ? tokens.primary : tokens.surface,
    color: activo ? tokens.textOnPrimary : tokens.text,
  });
  const conteo = (activo) => ({
    fontSize: 11.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
    color: activo ? tokens.textOnPrimary : tokens.textSecondary, opacity: activo ? 0.9 : 1,
  });
  return (
    <div
      role="group" aria-label="Filtrar por rubro"
      style={{
        display: 'flex', gap: 6, overflowX: 'auto', WebkitOverflowScrolling: 'touch',
        padding: '2px 2px 8px', marginBottom: 6, scrollbarWidth: 'thin', ...style,
      }}
    >
      <button type="button" onClick={() => setRubro(T)} aria-pressed={rubro === T} style={chip(rubro === T)}>
        Todos <span style={conteo(rubro === T)}>{total}</span>
      </button>
      {conteos.map(o => (
        <button
          key={o.id} type="button" aria-pressed={rubro === o.id}
          onClick={() => setRubro(rubro === o.id ? T : o.id)}
          style={chip(rubro === o.id)}
        >
          {o.etiqueta} <span style={conteo(rubro === o.id)}>{o.n}</span>
        </button>
      ))}
    </div>
  );
}

// El texto que sustituye a la pantalla en blanco cuando el rubro elegido no tiene nada.
export function AvisoVacioRubro({ filtro, busqueda = '', que = 'productos', style }) {
  if (!filtro.activo || filtro.visibles.length > 0) return null;
  const q = String(busqueda || '').trim();
  return (
    <div style={{
      padding: '18px 14px', textAlign: 'center', fontSize: 13, color: tokens.textSecondary,
      background: tokens.surfaceSubtle, border: `1px dashed ${tokens.border}`, borderRadius: 12, ...style,
    }} aria-live="polite">
      No hay {que} de <strong style={{ color: tokens.text }}>{filtro.etiqueta}</strong>
      {q ? <> que coincidan con «{q}»</> : ' aquí'}.{' '}
      <button type="button" onClick={filtro.limpiar} style={{ border: 'none', background: 'none', padding: 0, color: tokens.primary, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, textDecoration: 'underline' }}>
        Ver todos los rubros
      </button>
    </div>
  );
}
